import axios from 'axios';
import useIsVideoBuffering from 'Hooks/Editor/useIsVideoBuffering';
import useFetchBrandGroups from 'Hooks/Http/Brands/useFetchBrandGroups';
import useFetchProject from 'Hooks/Http/Projects/useFetchProject';
import useFetchEditorDetections from 'Hooks/Http/Videos/Detections/useFetchEditorDetections';
import useFetchFrameDetections from 'Hooks/Http/Videos/Detections/useFetchFrameDetections';
import useFetchPlacements from 'Hooks/Http/Videos/Placements/useFetchPlacements';
import useFetchVideo from 'Hooks/Http/Videos/useFetchVideo';
import useAppSelector from 'Hooks/Redux/useAppSelector';
import useLoadingDispatch from 'Hooks/Redux/useLoadingDispatch';
import useIndexDispatch from 'Hooks/Redux/useIndexDispatch';
import useMessagesDispatch from 'Hooks/Redux/useMessagesDispatch';
import useValueRef from 'Hooks/useValueRef';
import Brand from 'Models/Brands/Brand';
import BrandGroup from 'Models/Brands/BrandGroup';
import {
	DetectionFilterCriteria,
	emptyDetectionFilterCriteria,
} from 'Models/Videos/Detections/DetectionFilterCriteria';
import EditorVideoDetection from 'Models/Videos/Detections/EditorVideoDetection';
import {
	EditorVideoNewDetectionWithNullableIds,
	isEditorVideoNewDetection,
	VideoDetectionTypeDto,
} from 'Models/Videos/Detections/EditorVideoNewDetection';
import VideoDetectionCoordinate from 'Models/Videos/Detections/VideoDetectionCoordinate';
import VideoFrameDetection from 'Models/Videos/Detections/VideoFrameDetection';
import Video from 'Models/Videos/Video';
import VideoDetectionPlacement from 'Models/Videos/VideoDetectionPlacement';
import {
	Dispatch,
	MutableRefObject,
	SetStateAction,
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { getCurrentPointFromNewDetectionCoordinates } from 'Utils/DetectionHelpers';
import { getTransformDetectionTimeErrorMargin } from 'Utils/Http/EnvReader';
import { FrameDetectionsPerFrame } from 'Utils/Http/Requests/Videos/Detections/GetFrameDetectionsRequest';
import postMatchingDetectionsRequest from 'Utils/Http/Requests/Videos/Detections/PostMatchingDetectionsRequest';
import postNewDetectionRequest from 'Utils/Http/Requests/Videos/Detections/PostNewDetectionRequest';
import {
	deleteToken,
	getEditorFilters,
	getToken,
	setEditorFilters,
	getPlacementChangeCriteria,
	setPlacementChangeCriteria as savePlacementChangeCriteria,
} from 'Utils/LocalStorageHelper';
import { createNewGuid } from 'Utils/uuid';

export enum EditorMode {
	NORMAL,
	NEW_DETECTION,
}

export enum PlayMode {
	NORMAL,
	SECONDS,
}

export enum VideoPlayerDetailsAreaMode {
	DETECTIONS,
	FILTERS,
	CHANGE_ALL_PLACEMENT,
}

export interface UseVideoPlayerInstance {
	video: Video | null;
	frameDetections: FrameDetectionsPerFrame;
	placements: VideoDetectionPlacement[];
	loading: boolean;
	isFiltering: boolean;
	filterStatus: 'pending' | 'success' | 'error' | null;
	videoNode: HTMLVideoElement | null;
	setVideoNode: Dispatch<SetStateAction<HTMLVideoElement | null>>;
	isVideoPlaying: boolean;
	toggleVideoPlay: (play: boolean) => void;
	fps: number;
	selectDetectionIds: (detectionIds: number[], includePrevious: boolean) => void;
	unselectDetectionId: (detectionId: number) => void;
	selectedDetectionIds: number[];
	editorMode: EditorMode;
	detections: EditorVideoDetection[];
	getCurrentFrame: () => number;
	getCurrentFrameDetections: () => VideoFrameDetection[];
	brandGroups: BrandGroup[];
	brands: Brand[];
	showFilteredDetections: boolean;
	toggleShowFilteredDetections: () => void;
	playMode: PlayMode;
	togglePlayMode: () => void;
	retrievedTimeSpans: [number, number][];
	changeDetectionPlacement: (detectionId: number, placementId: number) => void;
	changeDetectionBrand: (detectionId: number, brandId: number) => void;
	setEditorMode: Dispatch<SetStateAction<EditorMode>>;
	addNewDetection: (point1: [number, number], point2: [number, number], scale: number) => void;
	newDetections: EditorVideoNewDetectionWithNullableIds[];
	selectNewDetections: (idxes: number[], includePrevious: boolean) => void;
	toggleNewDetectionSelection: (idx: number) => void;
	transformNewDetection: (coordinate: VideoDetectionCoordinate, idx: number) => void;
	setPlacementForNewDetections: (placementId: number) => void;
	setBrandForNewDetections: (brandId: number) => void;
	setTypeForNewDetections: (type: VideoDetectionTypeDto) => void;
	removeNewDetections: () => void;
	completeNewDetections: () => void;
	removeDetections: (detectionsIds: number[]) => void;
	clearDetections: () => void;
	currentTimeRef: MutableRefObject<number>;
	setCurrentTime: (value: number) => void;
	detailsAreaMode: VideoPlayerDetailsAreaMode;
	setDetailsAreaMode: Dispatch<SetStateAction<VideoPlayerDetailsAreaMode>>;
	placementChangeCriteria: DetectionFilterCriteria;
	setPlacementChangeCriteria: Dispatch<SetStateAction<DetectionFilterCriteria>>;
	filters: DetectionFilterCriteria;
	setFilters: Dispatch<SetStateAction<DetectionFilterCriteria>>;
	matchingDetectionIds: ReadonlySet<number> | null;
	isDetectionMatching: (id: number) => boolean;
}

const DEFAULT_FPS = 24;

const keepAvailableIds = (ids: number[], availableIds: ReadonlySet<number>): number[] => {
	const cleaned = ids.filter((id) => availableIds.has(id));
	return cleaned.length === ids.length ? ids : cleaned;
};

const useVideoPlayerInstance = (): UseVideoPlayerInstance => {
	const [videoNode, setVideoNode] = useState<HTMLVideoElement | null>(null);
	const [isVideoPlaying, setIsVideoPlaying] = useState(false);
	const [selectedDetectionIds, setSelectedDetectionIds] = useState<number[]>([]);
	const [editorMode, setEditorMode] = useState(EditorMode.NORMAL);
	const selectedVideoId = useAppSelector((state) => state.video.selectedVideoId);
	const [filterState, setFilterState] = useState(() => {
		const saved = selectedVideoId === null ? null : getEditorFilters(selectedVideoId);
		return { criteria: saved ?? emptyDetectionFilterCriteria, initialized: saved !== null };
	});
	const [placementChangeCriteria, setPlacementChangeCriteria] = useState<DetectionFilterCriteria>(
		getPlacementChangeCriteria
	);
	// Leave historical values intact until the reviewer edits the bulk configuration.
	const restoredPlacementChangeCriteria = useRef(placementChangeCriteria);
	const filters = filterState.criteria;
	const setFilters = useCallback<Dispatch<SetStateAction<DetectionFilterCriteria>>>((update) => {
		setFilterState((prev) => ({
			criteria: typeof update === 'function' ? update(prev.criteria) : update,
			initialized: true,
		}));
	}, []);
	const [showFilteredDetections, setShowFilteredDetections] = useState(false);
	const [playMode, setPlayMode] = useState(PlayMode.SECONDS);
	const [newDetections, setNewDetections] = useState<EditorVideoNewDetectionWithNullableIds[]>([]);
	const [forceRefreshEditorDetections, setForceRefreshEditorDetections] = useState(0);
	const [detailsAreaMode, setDetailsAreaMode] = useState<VideoPlayerDetailsAreaMode>(
		VideoPlayerDetailsAreaMode.DETECTIONS
	);

	const currentTimeRef = useRef(videoNode?.currentTime ?? 0);

	const { dispatchStartLoading, dispatchStopLoading } = useLoadingDispatch();
	const { dispatchLogout } = useIndexDispatch();
	const { dispatchSetAxiosErrorMessage } = useMessagesDispatch();
	const isBuffering = useIsVideoBuffering(videoNode);

	const token = useAppSelector<string | null>((state) => state.profile.token);

	const { data: video, loading: videoLoading } = useFetchVideo(selectedVideoId);
	const {
		frameDetections,
		isBuffering: frameDetectionsBuffering,
		retrievedTimeSpans,
		setFrameDetections,
		resetFetchedDetections,
		removeFrameDetections,
		refreshPendingDetections,
	} = useFetchFrameDetections(selectedVideoId, videoNode);
	const { data: placements, loading: placementsLoading, error: placementsError } = useFetchPlacements();
	const { data: brandGroups, loading: brandsLoading, error: brandsError } = useFetchBrandGroups();
	const { data: detections, loading: detectionsLoading, setData: setEditorDetectionsData } = useFetchEditorDetections(
		selectedVideoId,
		forceRefreshEditorDetections
	);
	const detectionsLoadingRef = useValueRef(detectionsLoading);

	const brands =
		useMemo(() => brandGroups?.groups.reduce((acc, x) => [...acc, ...x.brands], [] as Brand[]), [
			brandGroups?.groups,
		]) ?? null;

	const projectState = useFetchProject(video?.projectId ?? null);

	const frameDetectionsRef = useValueRef(frameDetections);

	const lastNewDetectionBrandIdRef = useRef<number | null>(null);
	const lastNewDetectionPlacementIdRef = useRef<number | null>(null);

	const loading = videoLoading || placementsLoading || detectionsLoading || brandsLoading;
	const fps = video?.fps ?? DEFAULT_FPS;

	useEffect(() => {
		if (isBuffering || frameDetectionsBuffering) dispatchStartLoading('Buffering');
		else dispatchStopLoading();
	}, [dispatchStartLoading, dispatchStopLoading, frameDetectionsBuffering, isBuffering]);

	// A keyed editor session restores once. Only an absent entry receives project defaults.
	useEffect(() => {
		if (token === null || video?.id !== selectedVideoId || brands === null || brandsLoading || brandsError !== null)
			return;
		if (
			video?.projectId != null &&
			(projectState.data?.id !== video.projectId || projectState.loading || projectState.error !== null)
		)
			return;
		setFilterState((prev) => {
			if (prev.initialized) return prev;
			const brandIds = brands.filter((x) => projectState.data?.brandGroupIds.includes(x.groupId)).map((x) => x.id);
			return {
				criteria: { ...emptyDetectionFilterCriteria, brand: video?.projectId == null ? null : { brandIds } },
				initialized: true,
			};
		});
	}, [
		brands,
		brandsError,
		brandsLoading,
		projectState.data,
		projectState.error,
		projectState.loading,
		selectedVideoId,
		token,
		video,
	]);

	useEffect(() => {
		if (token === null) return;
		const availableBrands = !brandsLoading && brandsError === null ? brands : null;
		const availablePlacements = !placementsLoading && placementsError === null ? placements : null;
		if (availableBrands === null && availablePlacements === null) return;
		setFilterState((prev) => {
			if (!prev.initialized) return prev;
			let { brand, placement, logoOrText } = prev.criteria;
			if (availableBrands !== null) {
				if (brand !== null) {
					const brandIds = keepAvailableIds(brand.brandIds, new Set(availableBrands.map((x) => x.id)));
					if (brandIds !== brand.brandIds) brand = { brandIds };
				}
				if (logoOrText !== null) {
					const applicableBrands = availableBrands.filter((x) => brand === null || brand.brandIds.includes(x.id));
					const logoIds = keepAvailableIds(
						logoOrText.logoIds,
						new Set(applicableBrands.flatMap((x) => x.logos.map((logo) => logo.id)))
					);
					const textIds = keepAvailableIds(
						logoOrText.textIds,
						new Set(applicableBrands.flatMap((x) => x.texts.map((text) => text.id)))
					);
					if (logoIds !== logoOrText.logoIds || textIds !== logoOrText.textIds) logoOrText = { logoIds, textIds };
				}
			}
			if (availablePlacements !== null && placement !== null) {
				const placementIds = keepAvailableIds(placement.placementIds, new Set(availablePlacements.map((x) => x.id)));
				if (placementIds !== placement.placementIds) placement = { placementIds };
			}
			if (
				brand === prev.criteria.brand &&
				placement === prev.criteria.placement &&
				logoOrText === prev.criteria.logoOrText
			)
				return prev;
			return { ...prev, criteria: { ...prev.criteria, brand, placement, logoOrText } };
		});
	}, [
		brands,
		brandsError,
		brandsLoading,
		filterState.initialized,
		placements,
		placementsError,
		placementsLoading,
		token,
	]);

	// Delayed option cleanup must not recreate preferences after logout clears storage.
	useEffect(() => {
		if (token !== null && getToken() !== null && selectedVideoId !== null && filterState.initialized)
			setEditorFilters(selectedVideoId, filters);
	}, [filterState.initialized, filters, selectedVideoId, token]);

	useEffect(() => {
		if (token !== null && getToken() !== null && placementChangeCriteria !== restoredPlacementChangeCriteria.current)
			savePlacementChangeCriteria(placementChangeCriteria, !brandsLoading && brandsError === null ? brands : null);
	}, [placementChangeCriteria, brands, brandsLoading, brandsError, token]);

	const [matchingIds, setMatchingIds] = useState<number[] | null>(null);
	const [matchingRevision, setMatchingRevision] = useState(0);
	const refreshMatchingDetections = useCallback(() => {
		refreshPendingDetections();
		if (detectionsLoadingRef.current) setForceRefreshEditorDetections((prev) => prev + 1);
		setMatchingRevision((prev) => prev + 1);
	}, [detectionsLoadingRef, refreshPendingDetections]);
	const [filterStatus, setFilterStatus] = useState<UseVideoPlayerInstance['filterStatus']>(null);
	const isFiltering = filterStatus === 'pending';

	const matchingDetectionIds = useMemo(() => (matchingIds === null ? null : new Set(matchingIds)), [matchingIds]);

	const isDetectionMatching = useCallback((id: number): boolean => matchingDetectionIds?.has(id) ?? true, [
		matchingDetectionIds,
	]);
	const matchingSelectedDetectionIds = useMemo(() => selectedDetectionIds.filter(isDetectionMatching), [
		isDetectionMatching,
		selectedDetectionIds,
	]);
	useEffect(() => {
		if (matchingDetectionIds === null) return;
		setSelectedDetectionIds((prev) => prev.filter((id) => matchingDetectionIds.has(id)));
	}, [matchingDetectionIds]);

	useEffect(() => {
		if (!isVideoPlaying || playMode !== PlayMode.SECONDS || videoNode === null) return;

		const interval = window.setInterval(() => {
			const currentTime = videoNode.currentTime;
			videoNode.currentTime = Math.floor(currentTime + 1);
		}, 1000); // Every second.
		return () => {
			window.clearInterval(interval);
		};
	}, [isVideoPlaying, playMode, videoNode]);

	// The effect will update the end times for all new detections when video time will change.
	useEffect(() => {
		if (videoNode === null || newDetections.length === 0) return;

		videoNode.ontimeupdate = () => {
			setNewDetections((prev) =>
				prev.map((x) => {
					const modifiedCoordinates = [...x.coordinates];
					modifiedCoordinates[x.coordinates.length - 1] = {
						...modifiedCoordinates[x.coordinates.length - 1],
						endTime: Math.ceil(currentTimeRef.current * 1000),
					};
					const modified: EditorVideoNewDetectionWithNullableIds = {
						...x,
						coordinates: modifiedCoordinates,
					};
					return modified;
				})
			);
		};
	}, [newDetections.length, videoNode]);

	useEffect(() => {
		if (videoNode === null) return;

		const timeUpdatedFunction = (): void => {
			if (!isBuffering) currentTimeRef.current = videoNode.currentTime;
		};
		if (!isBuffering) currentTimeRef.current = videoNode.currentTime;

		videoNode.addEventListener('timeupdate', timeUpdatedFunction);
		return () => {
			videoNode.removeEventListener('timeupdate', timeUpdatedFunction);
		};
	}, [isBuffering, videoNode]);

	const getCurrentFrame = useCallback((): number => {
		if (videoNode === null) return 0;

		return Math.ceil(currentTimeRef.current * fps);
	}, [fps, videoNode]);

	const getCurrentFrameDetections = useCallback((): VideoFrameDetection[] => {
		const currentFrame = getCurrentFrame();
		return frameDetectionsRef.current[currentFrame] ?? [];
	}, [frameDetectionsRef, getCurrentFrame]);

	const playbackGeneration = useRef(0);
	const filteringCycle = useRef<{ resumePlayback: boolean } | null>(null);
	const toggleVideoPlay = useCallback(
		(play: boolean): void => {
			if (videoNode === null || (play && filteringCycle.current !== null)) return;
			const generation = ++playbackGeneration.current;

			if (play && videoNode.paused) {
				if (playMode === PlayMode.NORMAL) {
					videoNode
						.play()
						.then(() => {
							if (playbackGeneration.current === generation) setIsVideoPlaying(true);
						})
						.catch(() => {
							if (playbackGeneration.current === generation) setIsVideoPlaying(false);
						});
				} else if (playMode === PlayMode.SECONDS) setIsVideoPlaying(true);
			} else if (!play) {
				if (playMode === PlayMode.NORMAL) videoNode.pause();
				setIsVideoPlaying(false);
			}
		},
		[playMode, videoNode]
	);

	const playbackRef = useValueRef({ isVideoPlaying, toggleVideoPlay, videoNode, playMode });
	const previousMatchingRequest = useRef<{ filters: DetectionFilterCriteria; videoId: number; token: string } | null>(
		null
	);
	useLayoutEffect(() => {
		if (selectedVideoId === null || token === null) return;
		const previous = previousMatchingRequest.current;
		const mutationRefresh =
			previous?.filters === filters && previous.videoId === selectedVideoId && previous.token === token;
		previousMatchingRequest.current = { filters, videoId: selectedVideoId, token };
		const explicitCycle = !mutationRefresh || filteringCycle.current !== null;
		let active = true;
		const cancellation = axios.CancelToken.source();
		if (explicitCycle && filteringCycle.current === null) {
			const playback = playbackRef.current;
			filteringCycle.current = {
				resumePlayback:
					playback.playMode === PlayMode.NORMAL
						? playback.videoNode !== null && !playback.videoNode.paused && !playback.videoNode.ended
						: playback.isVideoPlaying,
			};
			playbackRef.current.toggleVideoPlay(false);
		}
		setFilterStatus(explicitCycle ? 'pending' : null);
		const finish = (status: 'success' | 'error'): void => {
			if (!active) return;
			setFilterStatus(explicitCycle || status === 'error' ? status : null);
			const resume = filteringCycle.current?.resumePlayback;
			filteringCycle.current = null;
			if (resume) playbackRef.current.toggleVideoPlay(true);
		};
		const requestMatches = (): void => {
			postMatchingDetectionsRequest(token, selectedVideoId, filters, cancellation.token)
				.then((response) => {
					if (!active) return;
					setMatchingIds(response.data);
					finish('success');
				})
				.catch((error) => {
					if (!active) return;
					if (error.response?.status === 401 || error.response?.data?.code === 401) {
						deleteToken();
						dispatchLogout();
						return;
					}
					finish('error');
				});
		};
		const timer = mutationRefresh ? null : window.setTimeout(requestMatches, 2000);
		if (mutationRefresh) requestMatches();
		return () => {
			// Invalidate before aborting: even a response already received by the
			// transport must not finish a superseded cycle.
			active = false;
			cancellation.cancel();
			if (timer !== null) window.clearTimeout(timer);
		};
	}, [dispatchLogout, filters, matchingRevision, playbackRef, selectedVideoId, token]);

	useEffect(
		() => () => {
			playbackGeneration.current++;
			playbackRef.current.videoNode?.pause();
		},
		[playbackRef]
	);

	const selectDetectionIds = useCallback(
		(detectionIds: number[], includePrevious: boolean): void => {
			const eligibleIds = detectionIds.filter(isDetectionMatching);
			if (!includePrevious) setSelectedDetectionIds(eligibleIds);
			else
				setSelectedDetectionIds((prev) => Array.from(new Set([...prev.filter(isDetectionMatching), ...eligibleIds])));
		},
		[isDetectionMatching]
	);

	const unselectDetectionId = useCallback((detectionId: number): void => {
		setSelectedDetectionIds((prev) => prev.filter((x) => x !== detectionId));
	}, []);

	const toggleShowFilteredDetections = useCallback((): void => {
		setShowFilteredDetections((prev) => !prev);
	}, []);

	const togglePlayMode = useCallback((): void => {
		// If video is not loaded it makes no sense to change anything.
		if (videoNode === null) return;

		// We had normal play up till now.
		if (playMode === PlayMode.NORMAL) {
			setPlayMode(PlayMode.SECONDS);
			if (!videoNode.paused) videoNode.pause();
		} else if (playMode === PlayMode.SECONDS) {
			setPlayMode(PlayMode.NORMAL);
			if (isVideoPlaying) videoNode.play();
		}
	}, [isVideoPlaying, playMode, videoNode]);

	const changeEditorDetectionPlacement = useCallback(
		(detectionId: number, placementId: number): void => {
			setEditorDetectionsData((prev) => {
				if (prev === null) return prev;

				return prev.map((x) => {
					if (x.id === detectionId) {
						return {
							...x,
							placementId,
						};
					}
					return x;
				});
			});
		},
		[setEditorDetectionsData]
	);

	const changeEditorDetectionBrand = useCallback(
		(detectionId: number, brandId: number): void => {
			setEditorDetectionsData((prev) => {
				if (prev === null) return prev;

				return prev.map((x) => {
					if (x.id === detectionId) {
						return {
							...x,
							brandId,
						};
					}
					return x;
				});
			});
		},
		[setEditorDetectionsData]
	);

	const changeFrameDetectionPlacement = useCallback(
		(detectionId: number, placementId: number): void => {
			setFrameDetections((prev) => {
				const copiedPrev: FrameDetectionsPerFrame = { ...prev };
				Object.keys(copiedPrev).forEach((k) => {
					const numberKey = Number(k);
					copiedPrev[numberKey] = copiedPrev[numberKey].map((x) => {
						if (x.videoDetectionId === detectionId) return { ...x, placementId };
						return x;
					});
				});
				return copiedPrev;
			});
		},
		[setFrameDetections]
	);

	const changeFrameDetectionBrand = useCallback(
		(detectionId: number, brandId: number): void => {
			setFrameDetections((prev) => {
				const copiedPrev: FrameDetectionsPerFrame = { ...prev };
				Object.keys(copiedPrev).forEach((k) => {
					const numberKey = Number(k);
					copiedPrev[numberKey] = copiedPrev[numberKey].map((x) => {
						if (x.videoDetectionId === detectionId) return { ...x, brandId };
						return x;
					});
				});
				return copiedPrev;
			});
		},
		[setFrameDetections]
	);

	const changeDetectionPlacement = useCallback(
		(detectionId: number, placementId: number): void => {
			changeEditorDetectionPlacement(detectionId, placementId);
			changeFrameDetectionPlacement(detectionId, placementId);
			refreshMatchingDetections();
		},
		[changeEditorDetectionPlacement, changeFrameDetectionPlacement, refreshMatchingDetections]
	);

	const changeDetectionBrand = useCallback(
		(detectionId: number, brandId: number): void => {
			changeEditorDetectionBrand(detectionId, brandId);
			changeFrameDetectionBrand(detectionId, brandId);
			refreshMatchingDetections();
		},
		[changeEditorDetectionBrand, changeFrameDetectionBrand, refreshMatchingDetections]
	);

	const addNewDetection = useCallback(
		(point1: [number, number], point2: [number, number], scale: number): void => {
			if (selectedVideoId === null) {
				alert('No video is selected.');
				return;
			}
			if (videoNode === null) {
				alert('Video is not yet initialized');
				return;
			}
			const { left, top } = videoNode.getBoundingClientRect();

			const coordinate: VideoDetectionCoordinate = {
				point1: { x: (point1[0] - left) / scale, y: (point1[1] - top) / scale },
				point2: { x: (point2[0] - left) / scale, y: (point1[1] - top) / scale },
				point3: { x: (point2[0] - left) / scale, y: (point2[1] - top) / scale },
				point4: { x: (point1[0] - left) / scale, y: (point2[1] - top) / scale },
			};
			const timeInMs = Math.ceil(currentTimeRef.current * 1000);

			const newDetection: EditorVideoNewDetectionWithNullableIds = {
				clientId: createNewGuid(),
				brandId: lastNewDetectionBrandIdRef.current,
				placementId: lastNewDetectionPlacementIdRef.current,
				videoId: selectedVideoId,
				type: VideoDetectionTypeDto.Logo,
				coordinates: [
					{
						coordinate,
						endTime: timeInMs,
						startTime: timeInMs,
					},
				],
				selected: false,
			};
			setNewDetections((prev) => [...prev, newDetection]);
		},
		[selectedVideoId, videoNode]
	);

	const selectNewDetection = useCallback((idxes: number[], includePrevious: boolean): void => {
		if (idxes.length < 0) return;

		setNewDetections((prev) => {
			return prev.map((x, i) => {
				if (idxes.includes(i)) {
					return {
						...x,
						selected: true,
					};
				}

				return {
					...x,
					selected: includePrevious ? x.selected : false,
				};
			});
		});
	}, []);

	const toggleNewDetectionSelection = useCallback((idx: number): void => {
		if (idx < 0) return;

		setNewDetections((prev) => {
			if (idx >= prev.length) return prev;
			return prev.map((x, i) => {
				if (i === idx) {
					return {
						...x,
						selected: !x.selected,
					};
				}
				return x;
			});
		});
	}, []);

	const transformNewDetection = useCallback(
		(coordinate: VideoDetectionCoordinate, idx: number): void => {
			if (videoNode === null) return;

			setNewDetections((prev) => {
				const detections = [...prev];
				const newDetectionCoordinates = [...prev[idx].coordinates];
				const [currentCoordinate, coordinateIdx] = getCurrentPointFromNewDetectionCoordinates(
					newDetectionCoordinates,
					currentTimeRef.current
				);

				const timeInMs = Math.ceil(currentTimeRef.current * 1000);
				const errorMargin = getTransformDetectionTimeErrorMargin();

				// 1. Začetni čas je znotraj napake -> posodobi točke trenutnega
				if (Math.abs(currentCoordinate.startTime - timeInMs) <= errorMargin) {
					currentCoordinate.coordinate = coordinate; // Works cus reference type
				}
				// 2. Končni čas je znotraj napake in naslednji obstaja -> posodobi točke naslednjega
				else if (
					Math.abs(currentCoordinate.endTime - timeInMs) <= errorMargin &&
					coordinateIdx + 1 < newDetectionCoordinates.length
				) {
					newDetectionCoordinates[coordinateIdx + 1].coordinate = coordinate;
				}
				// 3. Naredi novega
				else {
					const endTime = currentCoordinate.endTime;
					currentCoordinate.endTime = timeInMs;
					newDetectionCoordinates.splice(coordinateIdx + 1, 0, {
						endTime,
						coordinate,
						startTime: timeInMs,
					});
				}
				detections[idx].coordinates = newDetectionCoordinates;
				return detections;
			});
		},
		[videoNode]
	);

	const setPlacementForNewDetections = useCallback((placementId: number): void => {
		lastNewDetectionPlacementIdRef.current = placementId;
		setNewDetections((prev) =>
			prev.map((x) => {
				if (x.selected) {
					return {
						...x,
						placementId,
					};
				}
				return x;
			})
		);
	}, []);

	const setBrandForNewDetections = useCallback((brandId: number): void => {
		lastNewDetectionBrandIdRef.current = brandId;
		setNewDetections((prev) =>
			prev.map((x) => {
				if (x.selected) {
					return {
						...x,
						brandId,
					};
				}
				return x;
			})
		);
	}, []);

	const setTypeForNewDetections = useCallback((type: VideoDetectionTypeDto): void => {
		setNewDetections((prev) =>
			prev.map((x) => {
				if (x.selected) {
					return {
						...x,
						type,
					};
				}
				return x;
			})
		);
	}, []);

	const removeNewDetections = useCallback((): void => {
		setNewDetections((prev) => prev.filter((x) => !x.selected));
	}, []);

	const clearDetections = useCallback((): void => {
		resetFetchedDetections();
		setForceRefreshEditorDetections((prev) => prev + 1);
		setSelectedDetectionIds([]);
		refreshMatchingDetections();
	}, [refreshMatchingDetections, resetFetchedDetections]);

	const completeNewDetections = useCallback((): void => {
		if (video === null) {
			alert('Unable to save new detections. Video does not seems to be loaded.');
			return;
		}

		const detectionsToSave = newDetections.filter((x) => x.selected && x.brandId !== null && x.placementId !== null);
		dispatchStartLoading('Saving new detections.');
		const promises = detectionsToSave.map(async (draft) => {
			if (!isEditorVideoNewDetection(draft)) return;
			const { clientId, selected, ...request } = draft;
			try {
				await postNewDetectionRequest(token, request, video.id);
				setNewDetections((prev) => prev.filter((x) => x.clientId !== clientId));
				clearDetections();
			} catch (error) {
				dispatchSetAxiosErrorMessage(error);
			}
		});
		Promise.all(promises).finally(() => {
			dispatchStopLoading();
		});
	}, [
		dispatchSetAxiosErrorMessage,
		dispatchStartLoading,
		dispatchStopLoading,
		newDetections,
		clearDetections,
		token,
		video,
	]);

	const removeSelectedDetections = useCallback(
		(detectionIds: number[]): void => {
			// 1. Remove them from frame detections.
			removeFrameDetections(detectionIds);
			// 2. Remove the from editor detections
			setEditorDetectionsData((prev) => {
				if (prev === null) return null;
				return prev.filter((x) => !detectionIds.includes(x.id));
			});
			// 3. Remove them from selected array
			setSelectedDetectionIds((prev) => prev.filter((id) => !detectionIds.includes(id)));
			refreshMatchingDetections();
		},
		[refreshMatchingDetections, removeFrameDetections, setEditorDetectionsData]
	);

	const setCurrentTime = useCallback(
		(value: number): void => {
			if (videoNode === null) return;
			videoNode.currentTime = value;
		},
		[videoNode]
	);

	return {
		video,
		frameDetections: frameDetections ?? [],
		placements: placements ?? [],
		loading,
		isFiltering,
		filterStatus,
		videoNode,
		setVideoNode,
		isVideoPlaying,
		toggleVideoPlay,
		fps,
		selectDetectionIds,
		selectedDetectionIds: matchingSelectedDetectionIds,
		editorMode: editorMode,
		detections: detections ?? [],
		getCurrentFrame,
		getCurrentFrameDetections,
		unselectDetectionId,
		brandGroups: brandGroups?.groups ?? [],
		brands: brands ?? [],
		showFilteredDetections,
		toggleShowFilteredDetections,
		playMode,
		togglePlayMode,
		retrievedTimeSpans,
		changeDetectionPlacement,
		changeDetectionBrand,
		setEditorMode,
		addNewDetection,
		newDetections,
		selectNewDetections: selectNewDetection,
		toggleNewDetectionSelection,
		transformNewDetection,
		setPlacementForNewDetections,
		setBrandForNewDetections,
		setTypeForNewDetections,
		removeNewDetections,
		completeNewDetections,
		removeDetections: removeSelectedDetections,
		clearDetections,
		currentTimeRef,
		setCurrentTime,
		detailsAreaMode,
		setDetailsAreaMode,
		filters,
		setFilters,
		placementChangeCriteria,
		setPlacementChangeCriteria,
		matchingDetectionIds,
		isDetectionMatching,
	};
};

export default useVideoPlayerInstance;
